import BaseProduct from './BaseProduct';
import { ProductId } from '../types';
import store, { getLayerOpacity } from '../state';
import { getTileUrl, getAvailableWindsTimes, getAvailableLenticularTimes, getClosestTimeStep, getLenticularWindGridUrl, appendAuthToken } from '../config';
import { fetchWindGrid, WindGridData } from '../utils/WindGridLoader';
import maplibregl from 'maplibre-gl';
import LayerManager from './LayerManager';

interface CanvasWindParticle {
  lon: number;
  lat: number;
  age: number;
  maxAge: number;
}

// Speed color gradient matching baseline
function getWindColor(speed: number, opacity: number): string {
  const stops = [
    { val: 0, r: 0, g: 80, b: 255 },
    { val: 5, r: 34, g: 197, b: 94 },
    { val: 10, r: 234, g: 179, b: 8 },
    { val: 15, r: 249, g: 115, b: 22 },
    { val: 20, r: 239, g: 68, b: 68 },
    { val: 25, r: 217, g: 70, b: 239 }
  ];

  if (speed <= stops[0].val) {
    return `rgba(${stops[0].r}, ${stops[0].g}, ${stops[0].b}, ${opacity})`;
  }
  if (speed >= stops[stops.length - 1].val) {
    return `rgba(${stops[stops.length - 1].r}, ${stops[stops.length - 1].g}, ${stops[stops.length - 1].b}, ${opacity})`;
  }

  let i = 0;
  while (i < stops.length - 1 && speed > stops[i + 1].val) {
    i++;
  }

  const s0 = stops[i];
  const s1 = stops[i + 1];
  const t = (speed - s0.val) / (s1.val - s0.val);

  const r = Math.round(s0.r + (s1.r - s0.r) * t);
  const g = Math.round(s0.g + (s1.g - s0.g) * t);
  const b = Math.round(s0.b + (s1.b - s0.b) * t);

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

// Special color palette mapping 720 hPa winds aloft speed to wave formation potential
function getLenticularWindColor(speed: number, opacity: number): string {
  const stops = [
    { val: 0, r: 0, g: 210, b: 255 },    // Cyan/Sky Blue
    { val: 8, r: 0, g: 210, b: 255 },    // Cyan
    { val: 15, r: 255, g: 159, b: 67 },  // Amber Gold
    { val: 25, r: 236, g: 72, b: 153 }   // Vivid Magenta/Coral
  ];

  if (speed <= stops[0].val) {
    return `rgba(${stops[0].r}, ${stops[0].g}, ${stops[0].b}, ${opacity})`;
  }
  if (speed >= stops[stops.length - 1].val) {
    return `rgba(${stops[stops.length - 1].r}, ${stops[stops.length - 1].g}, ${stops[stops.length - 1].b}, ${opacity})`;
  }

  let i = 0;
  while (i < stops.length - 1 && speed > stops[i + 1].val) {
    i++;
  }

  const s0 = stops[i];
  const s1 = stops[i + 1];
  const t = (speed - s0.val) / (s1.val - s0.val);

  const r = Math.round(s0.r + (s1.r - s0.r) * t);
  const g = Math.round(s0.g + (s1.g - s0.g) * t);
  const b = Math.round(s0.b + (s1.b - s0.b) * t);

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

// Precomputed color tables for color-bucketed particle batching
const BUCKET_COLORS_CACHE: Record<string, string[]> = {};
function getBucketColors(numBuckets: number, isLenticular: boolean): string[] {
  const key = `${numBuckets}_${isLenticular}`;
  if (!BUCKET_COLORS_CACHE[key]) {
    const colors: string[] = [];
    const maxSpeed = 25.0;
    for (let i = 0; i < numBuckets; i++) {
      const speed = ((i + 0.5) / numBuckets) * maxSpeed;
      colors.push(isLenticular ? getLenticularWindColor(speed, 0.9) : getWindColor(speed, 1.0));
    }
    BUCKET_COLORS_CACHE[key] = colors;
  }
  return BUCKET_COLORS_CACHE[key];
}

// Web Mercator (EPSG:3857) normalized Y coordinate [0, 1] matching MapLibre GL
function latToMercatorY(lat: number): number {
  const clampedLat = Math.max(-85.05112878, Math.min(85.05112878, lat));
  const sin = Math.sin((clampedLat * Math.PI) / 180.0);
  const clampedSin = Math.max(-0.999999, Math.min(0.999999, sin));
  return 0.5 - (Math.atanh(clampedSin) / (2.0 * Math.PI));
}

// Inverse Web Mercator: converts normalized Y [0, 1] back to geodetic latitude in degrees
function mercatorYtoLat(y: number): number {
  const m = Math.PI * (1.0 - 2.0 * y);
  return (Math.asin(Math.tanh(m)) * 180.0) / Math.PI;
}

export class WindsLayer extends BaseProduct {
  public readonly id: ProductId = 'winds';
  
  // Primary display canvas
  private canvas: HTMLCanvasElement | null = null;
  private ctx: CanvasRenderingContext2D | null = null;

  // Dual offscreen ping-pong buffers for seamless periodic zero-alpha reset
  private bufferA: HTMLCanvasElement | null = null;
  private ctxA: CanvasRenderingContext2D | null = null;
  private bufferB: HTMLCanvasElement | null = null;
  private ctxB: CanvasRenderingContext2D | null = null;

  private activeBuffer: 'A' | 'B' = 'A';
  private cycleStartTime = Date.now();
  private readonly cycleDuration = 30000; // 30s cycle
  private readonly crossfadeDuration = 2500; // 2.5s transition

  // Simulation state
  private windGridData: WindGridData | null = null;
  private particlesA: CanvasWindParticle[] = [];
  private particlesB: CanvasWindParticle[] = [];
  private isRunning = false;
  private currentGridUrl = '';
  private refreshGeneration = 0;
  private animationFrameId: number | null = null;
  private unsubscribeStore: (() => void) | null = null;
  private lastFrameTime = 0;
  private bucketSegments: number[][] = [];

  private static instance: WindsLayer | null = null;
  public static getInstance(): WindsLayer | null {
    return WindsLayer.instance;
  }

  constructor(map: maplibregl.Map, layerManager: LayerManager) {
    super(map, layerManager);
    WindsLayer.instance = this;
    this.animate = this.animate.bind(this);
    
    // Main display canvas
    this.canvas = document.createElement('canvas');
    this.canvas.width = 1600;
    this.canvas.height = 1000;
    this.ctx = this.canvas.getContext('2d');

    // Dual ping-pong buffers
    this.bufferA = document.createElement('canvas');
    this.bufferA.width = 1600;
    this.bufferA.height = 1000;
    this.ctxA = this.bufferA.getContext('2d');

    this.bufferB = document.createElement('canvas');
    this.bufferB.width = 1600;
    this.bufferB.height = 1000;
    this.ctxB = this.bufferB.getContext('2d');
    
    console.log("WindsLayer: Canvas Instantiated. Canvas size:", this.canvas.width, "x", this.canvas.height);

    // Instant clear on pan/zoom start to prevent smearing across coordinates
    this.map.on('movestart', () => {
      this.clearAllBuffers();
    });

    this.map.on('move', () => {
      const state = store.getState();
      if (this.isRunning && this.windGridData && state.windParticlesPlaying === false) {
        this.drawFrame();
      }
    });

    this.setupStoreSubscription();
  }

  private clearAllBuffers() {
    if (this.ctx && this.canvas) {
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    }
    if (this.ctxA && this.bufferA) {
      this.ctxA.clearRect(0, 0, this.bufferA.width, this.bufferA.height);
    }
    if (this.ctxB && this.bufferB) {
      this.ctxB.clearRect(0, 0, this.bufferB.width, this.bufferB.height);
    }
  }

  private setupStoreSubscription() {
    if (this.unsubscribeStore) return;

    let lastPlaying = true;
    let lastVisible = true;
    let lastColor = 'speed';
    let lastSize = 'normal';
    let lastTrail = 'medium';
    let lastThrottle = 'unlimited';
    let lastBatch = 'high';
    let lastRes = 'high';

    this.unsubscribeStore = store.subscribe((newState) => {
      if (!this.isRunning) return;

      const isPlaying = newState.windParticlesPlaying !== false;
      const isVisible = newState.windParticlesVisible !== false;
      const colorMode = newState.windColorMode || 'speed';
      const sizeMode = newState.windParticleSize || 'normal';
      const trailMode = newState.windTrailLength || 'medium';
      const throttleMode = newState.windAnimationThrottle || 'unlimited';
      const batchMode = newState.windColorBatching || 'high';
      const resMode = newState.windCanvasResolution || 'high';

      // Handle play/pause toggling
      if (isPlaying !== lastPlaying) {
        lastPlaying = isPlaying;
        if (isPlaying) {
          if (!this.animationFrameId) this.animate();
        } else {
          if (this.animationFrameId) {
            cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = null;
          }
          this.drawFrame();
        }
      }

      // Handle visibility toggle
      if (isVisible !== lastVisible) {
        lastVisible = isVisible;
        if (!isVisible) {
          this.clearAllBuffers();
          this.map.triggerRepaint();
        } else {
          this.drawFrame();
        }
      }

      // Handle visual & performance variations trigger redraw
      if (
        colorMode !== lastColor ||
        sizeMode !== lastSize ||
        trailMode !== lastTrail ||
        throttleMode !== lastThrottle ||
        batchMode !== lastBatch ||
        resMode !== lastRes
      ) {
        lastColor = colorMode;
        lastSize = sizeMode;
        lastTrail = trailMode;
        lastThrottle = throttleMode;
        lastBatch = batchMode;
        if (resMode !== lastRes) {
          lastRes = resMode;
          this.clearAllBuffers();
        }
        if (!isPlaying) {
          this.drawFrame();
        }
      }
    });
  }

  protected onActivate(): void {
    console.log("WindsLayer: onActivate called.");
    this.isRunning = true;
    this.currentGridUrl = '';
    if (!this.unsubscribeStore) {
      this.setupStoreSubscription();
    }
    this.refresh();
  }

  protected onDeactivate(): void {
    console.log("WindsLayer: onDeactivate called.");
    this.isRunning = false;
    
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    const sourceId = 'wind-canvas-source';
    const layerId = 'wind-canvas-layer';

    if (this.map.getLayer(layerId)) {
      this.map.removeLayer(layerId);
    }
    if (this.map.getSource(sourceId)) {
      this.map.removeSource(sourceId);
    }

    this.currentGridUrl = '';
    this.windGridData = null;
    this.particlesA = [];
    this.particlesB = [];
    
    this.clearAllBuffers();

    if (this.unsubscribeStore) {
      this.unsubscribeStore();
      this.unsubscribeStore = null;
    }
  }

  protected onTimeUpdate(_date: string, _hour: string): void {
    this.refresh();
  }

  private createParticles(density: number): CanvasWindParticle[] {
    if (!this.windGridData) return [];
    const particles: CanvasWindParticle[] = [];
    
    const bounds = this.map.getBounds();
    const viewportWest = bounds.getWest();
    const viewportEast = bounds.getEast();
    const viewportNorth = Math.min(85.05112878, bounds.getNorth());
    const viewportSouth = Math.max(-85.05112878, bounds.getSouth());
    const isGlobal = (this.windGridData.east - this.windGridData.west) >= 359;

    let actualWest: number;
    let actualEast: number;
    let actualNorth = Math.min(viewportNorth, this.windGridData.north);
    let actualSouth = Math.max(viewportSouth, this.windGridData.south);

    if (isGlobal) {
      actualWest = viewportWest;
      actualEast = viewportEast;
    } else {
      const gridWest = this.windGridData.west;
      const gridEast = this.windGridData.east;
      const gridCenter = (gridWest + gridEast) / 2;
      const vpCenter = (viewportWest + viewportEast) / 2;
      const worldOffset = Math.round((vpCenter - gridCenter) / 360) * 360;

      const shiftedWest = gridWest + worldOffset;
      const shiftedEast = gridEast + worldOffset;

      const west = Math.max(viewportWest, shiftedWest);
      const east = Math.min(viewportEast, shiftedEast);
      actualWest = east > west ? west : shiftedWest;
      actualEast = east > west ? east : shiftedEast;
    }

    if (actualNorth <= actualSouth) {
      actualNorth = this.windGridData.north;
      actualSouth = this.windGridData.south;
    }

    const mNorth = latToMercatorY(actualNorth);
    const mSouth = latToMercatorY(actualSouth);

    for (let i = 0; i < density; i++) {
      let pLon = actualWest + Math.random() * (actualEast - actualWest);
      let mY = mNorth + Math.random() * (mSouth - mNorth);
      let pLat = mercatorYtoLat(mY);
      
      // Attempt to land on active wind cell (avoid dead calm/masked regions)
      for (let attempt = 0; attempt < 4; attempt++) {
        const w = this.getWindAt(pLon, pLat);
        if (w && w.speed >= 0.2) break;
        pLon = actualWest + Math.random() * (actualEast - actualWest);
        mY = mNorth + Math.random() * (mSouth - mNorth);
        pLat = mercatorYtoLat(mY);
      }

      particles.push({
        lon: pLon,
        lat: pLat,
        age: Math.random() * 100,
        maxAge: 50 + Math.random() * 100
      });
    }
    return particles;
  }

  private respawnParticle(p: CanvasWindParticle, vWest: number, vEast: number, vNorth: number, vSouth: number) {
    if (!this.windGridData) return;
    const isGlobal = (this.windGridData.east - this.windGridData.west) >= 359;

    let targetLon = vWest;
    let targetLat = vSouth;

    for (let attempt = 0; attempt < 5; attempt++) {
      if (isGlobal) {
        targetLon = vWest + Math.random() * (vEast - vWest);
        const north = Math.min(vNorth, this.windGridData.north);
        const south = Math.max(vSouth, this.windGridData.south);
        const actualN = south < north ? north : vNorth;
        const actualS = south < north ? south : vSouth;
        const mN = latToMercatorY(actualN);
        const mS = latToMercatorY(actualS);
        targetLat = mercatorYtoLat(mN + Math.random() * (mS - mN));
      } else {
        const gridWest = this.windGridData.west;
        const gridEast = this.windGridData.east;
        const gridCenter = (gridWest + gridEast) / 2;
        const vpCenter = (vWest + vEast) / 2;
        const worldOffset = Math.round((vpCenter - gridCenter) / 360) * 360;

        const shiftedWest = gridWest + worldOffset;
        const shiftedEast = gridEast + worldOffset;

        const west = Math.max(vWest, shiftedWest);
        const east = Math.min(vEast, shiftedEast);
        const north = Math.min(vNorth, this.windGridData.north);
        const south = Math.max(vSouth, this.windGridData.south);

        if (east > west && north > south) {
          targetLon = west + Math.random() * (east - west);
          const mN = latToMercatorY(north);
          const mS = latToMercatorY(south);
          targetLat = mercatorYtoLat(mN + Math.random() * (mS - mN));
        } else {
          targetLon = shiftedWest + Math.random() * (shiftedEast - shiftedWest);
          const mN = latToMercatorY(this.windGridData.north);
          const mS = latToMercatorY(this.windGridData.south);
          targetLat = mercatorYtoLat(mN + Math.random() * (mS - mN));
        }
      }

      const w = this.getWindAt(targetLon, targetLat);
      if (w && w.speed >= 0.2) break;
    }

    p.lon = targetLon;
    p.lat = Math.max(-85.05112878, Math.min(85.05112878, targetLat));
    p.age = 0;
    p.maxAge = 50 + Math.random() * 100;
  }

  private animate() {
    if (!this.isRunning || !this.windGridData) {
      this.animationFrameId = null;
      return;
    }

    const state = store.getState();
    if (state.windParticlesPlaying === false) {
      this.animationFrameId = null;
      return;
    }

    if (state.windAnimationThrottle === '30fps') {
      const now = performance.now();
      const elapsed = now - this.lastFrameTime;
      const frameInterval = 33.33; // 30 FPS target (33.33ms)
      if (elapsed < frameInterval) {
        this.animationFrameId = requestAnimationFrame(this.animate);
        return;
      }
      this.lastFrameTime = now - (elapsed % frameInterval);
    }

    this.drawFrame();
    this.animationFrameId = requestAnimationFrame(this.animate);
  }

  private stepBuffer(
    bufferCtx: CanvasRenderingContext2D,
    particles: CanvasWindParticle[],
    targetDensity: number,
    width: number,
    height: number,
    viewportWest: number,
    viewportEast: number,
    viewportNorth: number,
    viewportSouth: number,
    vpWidth: number,
    mercNorthY: number,
    mercHeight: number,
    speedMultiplier: number,
    lineWidth: number,
    fadeAlpha: number,
    isPlaying: boolean,
    isWhiteMode: boolean,
    primary: string | null,
    batchMode: 'low' | 'med' | 'high'
  ) {
    if (particles.length !== targetDensity) {
      const fresh = this.createParticles(targetDensity);
      particles.length = 0;
      for (let i = 0; i < fresh.length; i++) particles.push(fresh[i]);
    }

    // Natural exponential trail decay matching baseline
    bufferCtx.globalCompositeOperation = 'destination-out';
    bufferCtx.fillStyle = `rgba(0, 0, 0, ${fadeAlpha.toFixed(4)})`;
    bufferCtx.fillRect(0, 0, width, height);
    bufferCtx.globalCompositeOperation = 'source-over';

    bufferCtx.lineWidth = lineWidth;
    bufferCtx.lineCap = 'round';
    bufferCtx.lineJoin = 'round';

    const dt = 0.6;
    const speedScale = 4000.0;

    if (isWhiteMode) {
      bufferCtx.strokeStyle = 'rgba(255, 255, 255, 0.92)';
      bufferCtx.beginPath();
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        if (isPlaying) {
          p.age++;
          if (
            p.age > p.maxAge ||
            p.lon < viewportWest || p.lon > viewportEast ||
            p.lat < viewportSouth || p.lat > viewportNorth
          ) {
            this.respawnParticle(p, viewportWest, viewportEast, viewportNorth, viewportSouth);
            continue;
          }
          const wind = this.getWindAt(p.lon, p.lat);
          if (!wind || wind.speed < 0.2) {
            if (!wind || p.age > 10) {
              this.respawnParticle(p, viewportWest, viewportEast, viewportNorth, viewportSouth);
            }
            continue;
          }

          const x1 = ((p.lon - viewportWest) / vpWidth) * width;
          const y1 = ((latToMercatorY(p.lat) - mercNorthY) / mercHeight) * height;

          const lat_step = (wind.v / 111000.0) * speedMultiplier * dt * speedScale;
          const lon_step = (wind.u / (111000.0 * Math.cos((p.lat * Math.PI) / 180.0))) * speedMultiplier * dt * speedScale;

          p.lon += lon_step;
          p.lat = Math.max(-85.05112878, Math.min(85.05112878, p.lat + lat_step));

          const x2 = ((p.lon - viewportWest) / vpWidth) * width;
          const y2 = ((latToMercatorY(p.lat) - mercNorthY) / mercHeight) * height;

          if (isNaN(x1) || isNaN(y1) || isNaN(x2) || isNaN(y2)) continue;
          if (Math.abs(x2 - x1) < 0.1 && Math.abs(y2 - y1) < 0.1) continue;

          bufferCtx.moveTo(x1, y1);
          bufferCtx.lineTo(x2, y2);
        }
      }
      bufferCtx.stroke();
    } else {
      // Color-bucketed particle batching: low = 8 colors, med = 16 colors, high = 64 colors
      const numBuckets = batchMode === 'low' ? 8 : (batchMode === 'med' ? 16 : 64);
      for (let b = 0; b < numBuckets; b++) {
        if (!this.bucketSegments[b]) this.bucketSegments[b] = [];
        this.bucketSegments[b].length = 0;
      }

      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        if (isPlaying) {
          p.age++;
          if (
            p.age > p.maxAge ||
            p.lon < viewportWest || p.lon > viewportEast ||
            p.lat < viewportSouth || p.lat > viewportNorth
          ) {
            this.respawnParticle(p, viewportWest, viewportEast, viewportNorth, viewportSouth);
            continue;
          }
          const wind = this.getWindAt(p.lon, p.lat);
          if (!wind || wind.speed < 0.2) {
            if (!wind || p.age > 10) {
              this.respawnParticle(p, viewportWest, viewportEast, viewportNorth, viewportSouth);
            }
            continue;
          }

          const x1 = ((p.lon - viewportWest) / vpWidth) * width;
          const y1 = ((latToMercatorY(p.lat) - mercNorthY) / mercHeight) * height;

          const lat_step = (wind.v / 111000.0) * speedMultiplier * dt * speedScale;
          const lon_step = (wind.u / (111000.0 * Math.cos((p.lat * Math.PI) / 180.0))) * speedMultiplier * dt * speedScale;

          p.lon += lon_step;
          p.lat = Math.max(-85.05112878, Math.min(85.05112878, p.lat + lat_step));

          const x2 = ((p.lon - viewportWest) / vpWidth) * width;
          const y2 = ((latToMercatorY(p.lat) - mercNorthY) / mercHeight) * height;

          if (isNaN(x1) || isNaN(y1) || isNaN(x2) || isNaN(y2)) continue;
          if (Math.abs(x2 - x1) < 0.1 && Math.abs(y2 - y1) < 0.1) continue;

          // Assign segment to speed bucket
          const bIdx = Math.max(0, Math.min(numBuckets - 1, Math.floor((wind.speed / 25.0) * numBuckets)));
          this.bucketSegments[bIdx].push(x1, y1, x2, y2);
        }
      }

      const colors = getBucketColors(numBuckets, primary === 'lenticular');
      for (let b = 0; b < numBuckets; b++) {
        const seg = this.bucketSegments[b];
        const segLen = seg ? seg.length : 0;
        if (segLen > 0) {
          bufferCtx.strokeStyle = colors[b];
          bufferCtx.beginPath();
          for (let s = 0; s < segLen; s += 4) {
            bufferCtx.moveTo(seg[s], seg[s + 1]);
            bufferCtx.lineTo(seg[s + 2], seg[s + 3]);
          }
          bufferCtx.stroke();
        }
      }
    }
  }

  private drawFrame() {
    if (!this.canvas || !this.ctx || !this.windGridData || !this.bufferA || !this.ctxA || !this.bufferB || !this.ctxB) return;

    const state = store.getState();
    const primary = state.primaryProduct;
    const resMode = state.windCanvasResolution || 'high';
    const resScale = resMode === 'low' ? 0.5 : (resMode === 'med' ? 0.75 : 1.0);
    const batchMode = state.windColorBatching || 'high';

    const container = this.map.getContainer();
    const clientW = container.clientWidth || 1600;
    const clientH = container.clientHeight || 1000;
    const width = Math.max(320, Math.round(clientW * resScale));
    const height = Math.max(200, Math.round(clientH * resScale));

    if (this.canvas.width !== width || this.canvas.height !== height) {
      this.canvas.width = width;
      this.canvas.height = height;
      this.bufferA.width = width;
      this.bufferA.height = height;
      this.bufferB.width = width;
      this.bufferB.height = height;
      this.clearAllBuffers();
    }

    if (state.windParticlesVisible === false) {
      this.ctx.clearRect(0, 0, width, height);
      this.map.triggerRepaint();
      return;
    }

    // Get current map viewport bounds
    const bounds = this.map.getBounds();
    const viewportWest = bounds.getWest();
    const viewportEast = bounds.getEast();
    const viewportNorth = Math.min(85.05112878, bounds.getNorth());
    const viewportSouth = Math.max(-85.05112878, bounds.getSouth());
    const vpWidth = viewportEast - viewportWest;

    if (vpWidth <= 0 || viewportNorth <= viewportSouth) return;

    const mercNorthY = latToMercatorY(viewportNorth);
    const mercSouthY = latToMercatorY(viewportSouth);
    const mercHeight = mercSouthY - mercNorthY;
    if (mercHeight <= 0) return;

    // Update canvas source coordinates to match viewport
    const coords: [[number, number], [number, number], [number, number], [number, number]] = [
      [viewportWest, viewportNorth],
      [viewportEast, viewportNorth],
      [viewportEast, viewportSouth],
      [viewportWest, viewportSouth]
    ];

    const canvasSource = this.map.getSource('wind-canvas-source') as any;
    if (canvasSource && typeof canvasSource.setCoordinates === 'function') {
      canvasSource.setCoordinates(coords);
    }

    // 1. Density Configuration (Relative to baseline default)
    let targetDensity = 4500;
    let speedMultiplier = 0.15;

    if (primary === 'lenticular') {
      const base = 800;
      speedMultiplier = 0.06;
      if (state.windParticleDensityLevel === 'low') targetDensity = Math.round(base * 0.5);
      else if (state.windParticleDensityLevel === 'high') targetDensity = Math.round(base * 1.75);
      else targetDensity = base;
    } else {
      const base = state.windDensity || 4500;
      speedMultiplier = state.windSpeed || 0.15;
      if (state.windParticleDensityLevel === 'low') targetDensity = 2200;
      else if (state.windParticleDensityLevel === 'high') targetDensity = 7500;
      else targetDensity = base;
    }

    // 2. Line Width Configuration (Relative to baseline default, scaled for canvas resolution)
    // Default 'normal' is now the former 'fine' (1.5), and 'bold' is now former 'medium' (2.5)
    let lineWidth = 1.5;
    if (primary === 'lenticular') {
      const base = 1.0;
      if (state.windParticleSize === 'fine') lineWidth = 0.7;
      else if (state.windParticleSize === 'bold') lineWidth = 1.5;
      else lineWidth = base;
    } else {
      const base = 1.5;
      if (state.windParticleSize === 'fine') lineWidth = 1.0;
      else if (state.windParticleSize === 'bold') lineWidth = 2.5;
      else lineWidth = base;
    }
    lineWidth = Math.max(0.6, lineWidth * resScale);

    // 3. Trail Length / Tail Size Configuration (Relative to baseline fadeAlpha default)
    // Lower fadeAlpha = longer trail persistence. Former 'long' (0.022) is now 'medium' default
    let fadeAlpha = 0.022; // Baseline default for surface winds
    if (primary === 'lenticular') {
      const base = 0.04;
      if (state.windTrailLength === 'short') fadeAlpha = 0.08;
      else if (state.windTrailLength === 'long') fadeAlpha = 0.02;
      else fadeAlpha = base;
    } else {
      const base = 0.022;
      if (state.windTrailLength === 'short') fadeAlpha = 0.055;
      else if (state.windTrailLength === 'long') fadeAlpha = 0.010;
      else fadeAlpha = base;
    }

    const isPlaying = state.windParticlesPlaying !== false;
    const isWhiteMode = primary !== 'lenticular' && state.windColorMode === 'white';

    // 4. Ping-Pong Cycle Management for zero permanent streaks
    const now = Date.now();
    const elapsed = now - this.cycleStartTime;
    const transitionStart = this.cycleDuration - this.crossfadeDuration;

    if (elapsed >= this.cycleDuration) {
      // Flip active buffer and wipe old buffer completely to 0 alpha
      if (this.activeBuffer === 'A') {
        this.ctxA.clearRect(0, 0, width, height);
        this.particlesA.length = 0;
        this.activeBuffer = 'B';
      } else {
        this.ctxB.clearRect(0, 0, width, height);
        this.particlesB.length = 0;
        this.activeBuffer = 'A';
      }
      this.cycleStartTime = now;
    }

    const currentElapsed = now - this.cycleStartTime;
    const inTransition = currentElapsed >= transitionStart;

    if (!inTransition) {
      // Normal single-buffer phase
      if (this.activeBuffer === 'A') {
        this.stepBuffer(
          this.ctxA, this.particlesA, targetDensity, width, height,
          viewportWest, viewportEast, viewportNorth, viewportSouth, vpWidth, mercNorthY, mercHeight,
          speedMultiplier, lineWidth, fadeAlpha, isPlaying, isWhiteMode, primary, batchMode
        );
        this.ctx.clearRect(0, 0, width, height);
        this.ctx.drawImage(this.bufferA, 0, 0);
      } else {
        this.stepBuffer(
          this.ctxB, this.particlesB, targetDensity, width, height,
          viewportWest, viewportEast, viewportNorth, viewportSouth, vpWidth, mercNorthY, mercHeight,
          speedMultiplier, lineWidth, fadeAlpha, isPlaying, isWhiteMode, primary, batchMode
        );
        this.ctx.clearRect(0, 0, width, height);
        this.ctx.drawImage(this.bufferB, 0, 0);
      }
    } else {
      // Seamless cross-fade transition phase (2.5 seconds)
      const t = (currentElapsed - transitionStart) / this.crossfadeDuration;
      const alphaPrimary = Math.max(0, Math.min(1, 1.0 - t));
      const alphaIncoming = Math.max(0, Math.min(1, t));

      this.stepBuffer(
        this.ctxA, this.particlesA, targetDensity, width, height,
        viewportWest, viewportEast, viewportNorth, viewportSouth, vpWidth, mercNorthY, mercHeight,
        speedMultiplier, lineWidth, fadeAlpha, isPlaying, isWhiteMode, primary, batchMode
      );
      this.stepBuffer(
        this.ctxB, this.particlesB, targetDensity, width, height,
        viewportWest, viewportEast, viewportNorth, viewportSouth, vpWidth, mercNorthY, mercHeight,
        speedMultiplier, lineWidth, fadeAlpha, isPlaying, isWhiteMode, primary, batchMode
      );

      this.ctx.clearRect(0, 0, width, height);

      const [bufOld, alphaOld, bufNew, alphaNew] = this.activeBuffer === 'A'
        ? [this.bufferA, alphaPrimary, this.bufferB, alphaIncoming]
        : [this.bufferB, alphaPrimary, this.bufferA, alphaIncoming];

      this.ctx.globalAlpha = alphaOld;
      this.ctx.drawImage(bufOld, 0, 0);
      this.ctx.globalAlpha = alphaNew;
      this.ctx.drawImage(bufNew, 0, 0);
      this.ctx.globalAlpha = 1.0;
    }

    if (canvasSource && typeof canvasSource.play === 'function') {
      canvasSource.play();
    }
    this.map.triggerRepaint();
  }

  public getWindAt(lon: number, lat: number): { u: number; v: number; speed: number; gust: number } | null {
    if (!this.windGridData) return null;

    const west = this.windGridData.west;
    const east = this.windGridData.east;
    const south = this.windGridData.south;
    const north = this.windGridData.north;
    const isGlobal = (east - west) >= 359;

    let sampleLon = lon;
    if (isGlobal) {
      const span = east - west;
      sampleLon = ((((lon - west) % span) + span) % span) + west;
    } else {
      const gridCenter = (west + east) / 2;
      const worldOffset = Math.round((lon - gridCenter) / 360) * 360;
      sampleLon = lon - worldOffset;
    }

    const x_pct = (sampleLon - west) / (east - west);
    const y_pct = (lat - south) / (north - south);

    if (x_pct < 0 || x_pct > 1 || y_pct < 0 || y_pct > 1) return null;

    const gridW = this.windGridData.grid_width;
    const gridH = this.windGridData.grid_height;
    const u = this.windGridData.u;
    const v = this.windGridData.v;
    const gust = this.windGridData.gust || [];

    const col = x_pct * (gridW - 1);
    const row = y_pct * (gridH - 1);

    const c0 = Math.floor(col);
    const c1 = Math.min(c0 + 1, gridW - 1);
    const r0 = Math.floor(row);
    const r1 = Math.min(r0 + 1, gridH - 1);

    const idx00 = r0 * gridW + c0;
    const idx01 = r0 * gridW + c1;
    const idx10 = r1 * gridW + c0;
    const idx11 = r1 * gridW + c1;

    const u00 = u[idx00];
    const u01 = u[idx01];
    const u10 = u[idx10];
    const u11 = u[idx11];

    const v00 = v[idx00];
    const v01 = v[idx01];
    const v10 = v[idx10];
    const v11 = v[idx11];

    if (u00 === undefined || u01 === undefined || v00 === undefined || v11 === undefined) {
      return null;
    }

    const tc = col - c0;
    const tr = row - r0;

    let interpU = (1 - tr) * ((1 - tc) * u00 + tc * u01) + tr * ((1 - tc) * u10 + tc * u11);
    let interpV = (1 - tr) * ((1 - tc) * v00 + tc * v01) + tr * ((1 - tc) * v10 + tc * v11);

    let interpGust = 0;
    if (gust && gust.length > 0) {
      const g00 = gust[idx00];
      const g01 = gust[idx01];
      const g10 = gust[idx10];
      const g11 = gust[idx11];
      interpGust = (1 - tr) * ((1 - tc) * g00 + tc * g01) + tr * ((1 - tc) * g10 + tc * g11);
    } else {
      interpGust = Math.sqrt(interpU * interpU + interpV * interpV);
    }

    const speed = Math.sqrt(interpU * interpU + interpV * interpV);
    const state = store.getState();
    const currentProduct = state.primaryProduct;
    const isGustMode = (currentProduct !== 'lenticular' && state.windDisplayMode === 'gusts');

    if (isGustMode && speed > 0.01) {
      const scale = interpGust / speed;
      interpU *= scale;
      interpV *= scale;
    }

    return { u: interpU, v: interpV, speed: isGustMode ? interpGust : speed, gust: interpGust };
  }

  private resolveGridUrl(): string | null {
    const state = store.getState();
    const manifest = state.manifest;
    const current = state.currentTimeStep;
    if (!manifest || !current) return null;

    const primary = state.primaryProduct;

    if (primary === 'lenticular') {
      if (!state.showLenticularWinds) return null;

      const model = state.selectedModels['lenticular'] || 'ensemble';
      const mode = state.lenticularMode || 'cap';

      const availableSteps = getAvailableLenticularTimes(manifest, model);
      const closestStep = getClosestTimeStep(current, availableSteps);
      if (!closestStep) return null;

      const resolvedDateStr = closestStep.date;
      const resolvedHourStr = closestStep.hour.replace('Z', '');

      const modelData = manifest.lenticular?.available_by_model?.[model] || manifest.lenticular?.available?.[model] || {};
      const modeData = modelData[mode] || {};
      const hours = modeData[resolvedDateStr] || [];

      if (hours.includes(resolvedHourStr)) {
        return getLenticularWindGridUrl(manifest, model, mode, resolvedDateStr, resolvedHourStr);
      }
      return null;
    } else {
      const showWinds = state.windsEnabled;
      if (!showWinds) return null;

      let model = state.selectedModels['winds'] || 'nbm';
      if (model === 'blend' || model === 'blend_50') model = 'nbm';

      const windsConfig = manifest.winds?.available_by_model || manifest.winds?.available || {};
      const availableModels = Object.keys(windsConfig);
      if (availableModels.length > 0 && !availableModels.includes(model)) {
        model = availableModels[0];
      }

      const availableSteps = getAvailableWindsTimes(manifest, model);
      const closestStep = getClosestTimeStep(current, availableSteps);
      if (!closestStep) return null;

      const resolvedDateStr = closestStep.date;
      const resolvedHourStr = closestStep.hour.replace('Z', '');

      const modelData = manifest.winds?.available_by_model?.[model] || manifest.winds?.available?.[model] || {};
      const hours = modelData[resolvedDateStr] || [];

      if (hours.includes(resolvedHourStr)) {
        const tileUrl = getTileUrl(manifest, 'winds', model, resolvedDateStr, resolvedHourStr);
        return appendAuthToken(tileUrl.replace('/{z}/{x}/{y}.webp', '/wind_grid.json'));
      }
      return null;
    }
  }

  public async refresh() {
    if (!this.active) return;
    const state = store.getState();
    if (!state.windsEnabled) {
      this.removeWindsLayer();
      this.currentGridUrl = '';
      this.windGridData = null;
      return;
    }

    const gridUrl = this.resolveGridUrl();
    if (!gridUrl) {
      this.removeWindsLayer();
      this.currentGridUrl = '';
      this.windGridData = null;
      return;
    }

    // Dedup: if the resolved URL is already loaded/loading, skip re-fetching.
    if (gridUrl === this.currentGridUrl && this.windGridData) {
      this.ensureLayerPosition();
      return;
    }

    console.log("WindsLayer: Fetching grid from:", gridUrl);
    
    // Stop any running animation loop since we are loading a new grid
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    this.windGridData = null;
    this.currentGridUrl = gridUrl;

    const gen = ++this.refreshGeneration;

    try {
      const grid = await fetchWindGrid(gridUrl);

      if (gen !== this.refreshGeneration || !this.isRunning) return;

      if (grid && grid.u && grid.v && grid.u.length > 0) {
        console.log("WindsLayer: Wind grid loaded (points:", grid.u.length, "). Spawning particles...");
        this.windGridData = grid;
        this.particlesA.length = 0;
        this.particlesB.length = 0;
        this.clearAllBuffers();

        this.ensureLayerPosition();
        
        // Start animation loop if not running
        if (this.animationFrameId === null) {
          this.animate();
        }
      } else {
        throw new Error("Invalid or empty wind grid payload");
      }
    } catch (e) {
      if (gen === this.refreshGeneration) {
        console.warn("Failed to load wind grid from:", gridUrl, e);
        this.removeWindsLayer();
        this.currentGridUrl = '';
      }
    }
  }

  private ensureLayerPosition() {
    if (!this.windGridData || !this.canvas) return;

    const sourceId = 'wind-canvas-source';
    const layerId = 'wind-canvas-layer';

    const bounds = this.map.getBounds();
    const viewportWest = bounds.getWest();
    const viewportEast = bounds.getEast();
    const viewportNorth = Math.min(85.05112878, bounds.getNorth());
    const viewportSouth = Math.max(-85.05112878, bounds.getSouth());

    const coords: [[number, number], [number, number], [number, number], [number, number]] = [
      [viewportWest, viewportNorth],
      [viewportEast, viewportNorth],
      [viewportEast, viewportSouth],
      [viewportWest, viewportSouth]
    ];

    if (!this.map.getSource(sourceId)) {
      console.log("WindsLayer: Registering clamped canvas source with bounds:", coords);
      this.map.addSource(sourceId, {
        type: 'canvas',
        canvas: this.canvas,
        coordinates: coords,
        animate: true
      });
    } else {
      const canvasSource = this.map.getSource(sourceId) as any;
      if (canvasSource && typeof canvasSource.setCoordinates === 'function') {
        canvasSource.setCoordinates(coords);
      }
    }

    if (!this.map.getLayer(layerId)) {
      // Find the label layer to insert before, so labels stay on top
      let insertBefore = undefined;
      const layers = this.map.getStyle()?.layers;
      if (layers) {
        const labelLayer = layers.find(l => l.id.includes('label') || l.id.includes('place') || l.id.includes('symbol'));
        if (labelLayer) {
          insertBefore = labelLayer.id;
        }
      }

      console.log("WindsLayer: Adding canvas layer to map. Insert before:", insertBefore);
      this.map.addLayer({
        id: layerId,
        type: 'raster',
        source: sourceId,
        paint: {
          'raster-opacity': 1.0 * getLayerOpacity('winds', 1.0)
        }
      }, insertBefore);
    } else {
      // Ensure layer remains properly ordered (on top of other basemaps but below labels)
      try {
        let insertBefore = undefined;
        const layers = this.map.getStyle()?.layers;
        if (layers) {
          const labelLayer = layers.find(l => l.id.includes('label') || l.id.includes('place') || l.id.includes('symbol'));
          if (labelLayer) {
            insertBefore = labelLayer.id;
          }
        }
        
        const layerIdx = layers?.findIndex(l => l.id === layerId) ?? -1;
        const targetIdx = insertBefore ? layers?.findIndex(l => l.id === insertBefore) ?? -1 : -1;
        
        if (layerIdx !== -1 && targetIdx !== -1 && layerIdx > targetIdx) {
          this.map.moveLayer(layerId, insertBefore);
        }
      } catch (e) {
        console.warn("WindsLayer: moveLayer failed:", e);
      }
    }
  }

  private removeWindsLayer() {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    const sourceId = 'wind-canvas-source';
    const layerId = 'wind-canvas-layer';
    if (this.map.getLayer(layerId)) this.map.removeLayer(layerId);
    if (this.map.getSource(sourceId)) this.map.removeSource(sourceId);
    if (this.ctx && this.canvas) {
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    }
  }
}
export default WindsLayer;
